/* Copyright (c) 2020-present CloudIO Inc */

import {
  DBRow,
  IoAgentGroups,
  IoAgents,
  IoAppDevelopersV,
  IoApplications,
  IoApplicationsEdit,
  IoComponents,
  IoConnections,
  IoDatasource,
  IoDatasourceAccessV,
  IoDatasourceAttributes,
  IoDbScripts,
  IoEmailRequests,
  IoExternalDs,
  IoFunctions,
  IoImages,
  IoLanguages,
  IoLookups,
  IoLookupValues,
  IoMyNotifications,
  IoPageAccessV,
  IoPages,
  IoProfiles,
  IoRoles,
  IoScheduleRuns,
  IoStorageConnections,
  IoTestScripts,
  IoUiScripts,
  IoUserPagesV,
  IoUserProfile,
  IoUsers,
  IoWorkflowNodes,
  Query,
  Row,
} from '@cloudio-saas/datasource-types';
import { IoWfDefinitions } from '@cloudio-saas/workflow-nodes';
import { LookupValueType, Platform } from 'componentregistry';
import { useStoreRecords } from 'components/DataStoreReference/DataStoreReference';
import { Option } from 'custom/SelectInput';
import { WorkflowInstanceSource } from 'layout/Sidebar/atoms';
import { useEffect, useMemo } from 'react';
import Store, { QueryRequest } from 'store/Store';
import { Records } from 'store/storeTypes';
import { useStoreBusy } from 'store/useAppStore';
import useDataStore, { getStoreCreateIfNew } from 'store/useDataStore';
import { isNull } from 'utils/isEmpty';

const replaceKeyAndValue = <T>(
  oldKey: string,
  newKey: string,
  newValue: DBRow<T>,
  o: Records<T>,
): Records<T> => {
  const _obj = { ...o };
  delete _obj[oldKey];
  _obj[newKey] = newValue;
  return _obj;
};

const updateValue = (a: string[], oldValue: string, newValue: string) => {
  const _a: string[] = [...a];
  const index = _a.indexOf(oldValue);
  if (index !== -1) {
    _a[index] = newValue;
  }
  return _a;
};

const setValue = <T extends Record<string, unknown>>(
  a: T,
  key: keyof T,
  value: T[keyof T] | null | undefined,
): T => {
  const _a: T = { ...a };
  if (isNull(value)) {
    delete _a[key];
  } else {
    _a[key] = value;
  }
  return _a;
};

const removeKey = <T extends Record<string, unknown>>(a: T, key: string): T => {
  const _a = { ...a };
  delete _a[key];
  return _a;
};

const push = (a: string[], element: string, top: boolean): string[] => {
  const _a: string[] = Object.assign([], a);
  if (top) {
    _a.unshift(element);
  } else {
    _a.push(element);
  }
  return _a;
};

const inViewBox = (
  ref: React.RefObject<HTMLElement | null>,
  boxref: React.RefObject<HTMLElement | null>,
): boolean => {
  if (ref && boxref && ref.current && boxref.current) {
    const top = ref.current.getBoundingClientRect().y;
    const y1 = boxref.current.getBoundingClientRect().y;
    const y2 = y1 + boxref.current.getBoundingClientRect().height - 32;
    return top > y1 && top < y2;
  }
  return false;
};

interface StoreProps {
  appUid: string;
  pageId: string;
  ds: string;
  alias: string;
  maxRows?: number;
}

export function getDataSourceListStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasourceList',
    alias: `_page_datasources_alias_${appUid}`,
  };
}

export function getIoDatasourceStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasource',
    alias: `datasourcesAlias_${appUid}`,
  };
}

export function getIoDatasourceAccessVProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasourceAccessV',
    alias: 'IoDatasourceAccessV-alias',
  };
}

export function useIoDatasourceAccessVStore(appUid: string) {
  return useDataStore<IoDatasourceAccessV>(getIoDatasourceAccessVProps(appUid));
}

export function getIoAuditStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoAudit',
    alias: `IoAuditAlias_${appUid}`,
  };
}

export function getIoExternalDsStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoExternalDs',
    alias: `extDatasourcesAlias_${appUid}`,
  };
}

export function getIoLookupsStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoLookups',
    alias: `IoLookups_alias_${appUid}`,
  };
}

export function getIoUserPagesVStoreProps(): StoreProps {
  return {
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoUserPagesV',
    alias: 'IoUserPagesV-alias',
  };
}

export function useUserPagesStore() {
  return useDataStore<IoUserPagesV>(getIoUserPagesVStoreProps());
}

export function useUserAppsStore() {
  return useDataStore<IoUserPagesV>(getIoAppDevelopersVStoreProps());
}

export function getIoMyNotificationsProps(alias: string): StoreProps {
  const props: StoreProps = {
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoMyNotifications',
    alias,
    maxRows: 10,
  };
  return props;
}

export function useIoMyNotificationsStore(alias: string) {
  return useDataStore<IoMyNotifications>(getIoMyNotificationsProps(alias));
}

export function useUserPages(): DBRow<IoUserPagesV>[] {
  const store = useUserPagesStore();
  store.read({ props: { limit: 2000 } });
  store.setInitialQueryFired();
  return useStoreRecords(store) as DBRow<IoUserPagesV>[];
}

export function useUsers(): DBRow<IoUsers>[] {
  const store = useIoUsersStore('cloudio');
  store.read({ props: { limit: 2000 } });
  store.setInitialQueryFired();
  return useStoreRecords(store) as DBRow<IoUsers>[];
}

export function usePages(appUid: string): DBRow<IoPages>[] {
  const pagesStore = useDataStore<IoPages>(getIoPagesListStoreProps(appUid));
  pagesStore.read({ props: { limit: 2000 } });
  pagesStore.setInitialQueryFired();
  return useStoreRecords(pagesStore) as DBRow<IoPages>[];
}

export function getIoPagesListStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesListAlias',
  };
}

export function useUserApps(): DBRow<IoAppDevelopersV>[] {
  const store = useDataStore<IoAppDevelopersV>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoUserAppsV',
    alias: 'userAppsVHomePage',
  });
  store.read({
    props: {
      limit: 2000,
      fetchDistinct: true,
      projection: {
        appUid: 1,
        appName: 1,
        appIcon: 1,
        orgUid: 1,
        userName: 1,
      },
      sort: { appName: 1 },
    },
  });
  store.setInitialQueryFired();
  return useStoreRecords(store) as DBRow<IoAppDevelopersV>[];
}

export function useUserProfile(userName: string): DBRow<IoUserProfile> {
  const store = useDataStore<IoUserProfile>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoUserProfile',
    alias: 'IoUserProfile',
  });
  store.query({
    props: {
      filter: [{ userName: { is: userName } }],
    },
  });
  store.setInitialQueryFired();
  const records = useStoreRecords(store) as DBRow<IoUserProfile>[];
  return records[0];
}

export function getIoProfileValuesStoreProps(): StoreProps {
  return {
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoProfileValues',
    alias: 'IoProfileValues-alias',
  };
}

export function getIoLookupValuesStoreProps(
  appUid: string,
  lookupCode: string,
): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoLookupValues',
    alias: `IoLookupValues-${lookupCode}`,
  };
}

export function getIoPageAccessVStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPageAccessV',
    alias: 'IoPageAccessV-alias',
  };
}

export function useIoPageAccessVStore(appUid: string) {
  return useDataStore<IoPageAccessV>(getIoPageAccessVStoreProps(appUid));
}

export function useLookupValuesStore(appUid: string, lookup: string) {
  return useDataStore<IoLookupValues>(
    getIoLookupValuesStoreProps(appUid, lookup),
  );
}

export function getPagesStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: 'Sidebar',
    ds: 'IoPages',
    alias: `_app_pages_alias_${appUid}`,
  };
}

export function getLookupFilter(
  lookupUid: string,
): QueryRequest<IoLookupValues> {
  return {
    props: {
      filter: [{ lookupUid: { is: lookupUid } }],
      projection: {
        lookupLabel: 1,
        lookupValue: 1,
        lookupValueNum: 1,
        icon: 1,
        color: 1,
        description: 1,
        startDate: 1,
        endDate: 1,
      },
      sort: { lookupLabel: 1 },
      offset: 0,
      limit: 20000,
    },
  };
}

export interface LookupOption extends Option {
  value: string | number;
  label: string;
  icon?: string;
  color?: string;
}

export function useLookupValues(
  platform: Platform,
  appUid: string,
  lookup?: LookupValueType,
): [LookupOption[], boolean] {
  const store = lookup?.code
    ? getStoreCreateIfNew<IoLookupValues>({
        ...getIoLookupValuesStoreProps(appUid, lookup.code),
        platform,
      })
    : null;

  const records = useStoreRecords(store) as DBRow<IoLookupValues>[];

  const options: LookupOption[] = useMemo(() => {
    if (!records || !lookup?.code) return [];
    return records.map(
      (r) =>
        ({
          value: lookup.isNumeric ? r.lookupValueNum : r.lookupValue,
          label: r.lookupLabel,
          icon: r.icon,
          color: r.color,
          description: r.description,
          startDate: r.startDate,
          endDate: r.endDate,
        } as LookupOption),
    );
  }, [records, lookup]);

  useEffect(() => {
    if (!store || !lookup?.code) return;
    store.ignorePKDuplicate = true;
    store.query(getLookupFilter(lookup.code));
  }, [appUid, lookup, store]);

  if (store && !store.hasAutoQueryFired() && lookup?.code) {
    const s = store;
    platform.quietCallback(() => {
      s.setAutoQueryFired();
      if (!s.ignorePKDuplicate) s.ignorePKDuplicate = true;
      s.query(getLookupFilter(lookup.code));
    });
  }

  const isBusy = useStoreBusy(store);

  return [options, isBusy];
}

export function getIoLookupValuesStoreSidebarProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoLookupValues',
    alias: 'lookupValueAlias',
  };
}

export function useIoLookupValuesStore(appUid: string) {
  return useDataStore<IoLookupValues>(
    getIoLookupValuesStoreSidebarProps(appUid),
  );
}

export function useIoLookupsStore(appUid: string) {
  return useDataStore<IoLookups>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoLookups',
    alias: `lookupAlias_${appUid}`,
  });
}

export function getIoPatchHistoryStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPatchHistory',
    alias: `patchAlias_${appUid}`,
  };
}

export function getIoPagesStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: `IoPages-alias-${appUid}`,
  };
}

export function getIoPagesStore(
  platform: Platform,
  appUid: string,
): Store<IoPages> | null {
  const props = getIoPagesStoreProps(appUid);
  return platform.getStore<IoPages>(appUid, props.pageId, props.alias);
}

export function useIoPagesStore(appUid: string) {
  return useDataStore<IoPages>(getIoPagesStoreProps(appUid));
}

export function useIoImagesStore(appUid: string) {
  return useDataStore<IoImages>(getIoImagesStoreProps(appUid));
}

export function getIoImagesStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoImages',
    alias: `IoImages_alias_${appUid}`,
  };
}

export function getIoWfDefinitionsStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoWfDefinitions',
    alias: `IoWfDefinitions-alias-${appUid}`,
  };
}

export function getIoWfDefinitionsAgenticStoreProps(
  appUid: string,
): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoWfDefinitions',
    alias: `IoWfDefinitions-alias-agentic-${appUid}`,
  };
}

export function getIoWfDefinitionsStore(
  platform: Platform,
  appUid: string,
): Store<IoWfDefinitions> | null {
  const props = getIoWfDefinitionsStoreProps(appUid);
  return platform.getStore<IoWfDefinitions>(appUid, props.pageId, props.alias);
}

export function getIoWfDefinitionsEditStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoWfDefinitions',
    alias: 'IoWfDefinitions-edit',
  };
}

export function getIoWfDefinitionsEditStore(
  platform: Platform,
  appUid: string,
): Store<IoWfDefinitions> | null {
  const props = getIoWfDefinitionsEditStoreProps(appUid);
  return platform.getStore<IoWfDefinitions>(appUid, props.pageId, props.alias);
}

export function getIoWfDefinitionsAgenticEditStoreProps(
  appUid: string,
): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoWfDefinitions',
    alias: 'IoWfDefinitions-agentic-edit',
  };
}

export function getIoWfDefinitionsAgenticEditStore(
  platform: Platform,
  appUid: string,
): Store<IoWfDefinitions> | null {
  const props = getIoWfDefinitionsAgenticEditStoreProps(appUid);
  return platform.getStore<IoWfDefinitions>(appUid, props.pageId, props.alias);
}

export function getIoFunctionsStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoFunctions',
    alias: `IoFunctions-alias_${appUid}`,
  };
}

export function getIoFunctionsStore(
  platform: Platform,
  appUid: string,
): Store<IoFunctions> | null {
  const props = getIoFunctionsStoreProps(appUid);
  return platform.getStore<IoFunctions>(appUid, props.pageId, props.alias);
}

export function useIoFunctionsStore(appUid: string) {
  return useDataStore<IoFunctions>(getIoFunctionsStoreProps(appUid));
}

export function getIoDBScriptsStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDbScripts',
    alias: `IoDbScripts-alias_${appUid}`,
  };
}

export function getIoDBScriptsStore(
  platform: Platform,
  appUid: string,
): Store<IoDbScripts> | null {
  const props = getIoDBScriptsStoreProps(appUid);
  return platform.getStore<IoDbScripts>(appUid, props.pageId, props.alias);
}

export function useIoDBScriptsStore(appUid: string) {
  return useDataStore<IoDbScripts>(getIoDBScriptsStoreProps(appUid));
}

export function getIoDBScriptsEditStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDbScripts',
    alias: 'IoDbScripts-editAlias',
  };
}

export function useIoDBScriptsEditStore(appUid: string) {
  return useDataStore<IoDbScripts>(getIoDBScriptsEditStoreProps(appUid));
}

export function getIoDBScriptsEditStore(
  platform: Platform,
  appUid: string,
): Store<IoDbScripts> | null {
  const props = getIoDBScriptsEditStoreProps(appUid);
  return platform.getStore<IoDbScripts>(appUid, props.pageId, props.alias);
}

export function getIoUIScriptsStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoUiScripts',
    alias: `IoUiScripts-alias_${appUid}`,
  };
}

export function getIoUIScriptsStore(
  platform: Platform,
  appUid: string,
): Store<IoUiScripts> | null {
  const props = getIoUIScriptsStoreProps(appUid);
  return platform.getStore<IoUiScripts>(appUid, props.pageId, props.alias);
}

export function useIoUIScriptsStore(appUid: string) {
  return useDataStore<IoUiScripts>(getIoUIScriptsStoreProps(appUid));
}

export function getIoUIScriptsEditStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoUiScripts',
    alias: 'IoUiScripts-editAlias',
  };
}

export function useIoUIScriptsEditStore(appUid: string) {
  return useDataStore<IoUiScripts>(getIoUIScriptsEditStoreProps(appUid));
}

export function getIoUIScriptsEditStore(
  platform: Platform,
  appUid: string,
): Store<IoUiScripts> | null {
  const props = getIoUIScriptsEditStoreProps(appUid);
  return platform.getStore<IoUiScripts>(appUid, props.pageId, props.alias);
}

export function useJobsStore() {
  return useDataStore<IoScheduleRuns>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoScheduleRunsV',
    alias: 'IoScheduleRunsV-sidebar',
  });
}

export function getIoFunctionsEditStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoFunctions',
    alias: 'IoFunctions-editAlias',
  };
}

export function useIoFunctionsEditStore(appUid: string) {
  return useDataStore<IoFunctions>(getIoFunctionsEditStoreProps(appUid));
}

export function getIoFunctionsEditStore(
  platform: Platform,
  appUid: string,
): Store<IoFunctions> | null {
  const props = getIoFunctionsEditStoreProps(appUid);
  return platform.getStore<IoFunctions>(appUid, props.pageId, props.alias);
}

export function getIoFunctionsScheduledRunsStoreProps(
  appUid: string,
): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoFunctionsScheduledRuns',
    alias: 'IoFunctionsScheduledRuns-alias',
  };
}

export function getIoTestScriptsStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoTestScripts',
    alias: `IoTestScripts-alias_${appUid}`,
  };
}

export function getIoTestScriptsStore(
  platform: Platform,
  appUid: string,
): Store<IoTestScripts> | null {
  const props = getIoTestScriptsStoreProps(appUid);
  return platform.getStore<IoTestScripts>(appUid, props.pageId, props.alias);
}

export function useIoTestScriptsStore(appUid: string) {
  return useDataStore<IoTestScripts>(getIoTestScriptsStoreProps(appUid));
}

export function useIoDatasourceEditStore(appUid: string) {
  return useDataStore<IoDatasource>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasource',
    alias: 'datasourcesEditAlias',
  });
}

export function useIoExternalDsEditStore(appUid: string) {
  return useDataStore<IoExternalDs>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoExternalDs',
    alias: 'extDatasourcesEditAlias',
  });
}

export function getIoDatasourceAttributesStoreProps(
  appUid: string,
): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasourceAttributes',
    alias: 'datasourcesAttributesAlias',
  };
}

export function useIoDatasourceAttributesStore(appUid: string) {
  return useDataStore<IoDatasourceAttributes>(
    getIoDatasourceAttributesStoreProps(appUid),
  );
}

export function getIoPagesCopyStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesCopy-editAlias',
  };
}

export function useIoPagesCopyStore(appUid: string) {
  return useDataStore<IoPages>(getIoPagesCopyStoreProps(appUid));
}

export function getIoPagesEditStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPagesMinEdit',
    alias: 'IoPagesMinEdit-editAlias',
  };
}

export function useIoPagesEditStore(appUid: string) {
  return useDataStore<IoPages>(getIoPagesEditStoreProps(appUid));
}

export function getIoPagesEditStore(
  platform: Platform,
  appUid: string,
): Store<IoPages> | null {
  const props = getIoPagesEditStoreProps(appUid);
  return platform.getStore<IoPages>(appUid, props.pageId, props.alias);
}

export function getIoUsersStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoUsers',
    alias: 'IoUsers-alias',
  };
}

export function useIoUsersStore(appUid: string) {
  return useDataStore<IoUsers>(getIoUsersStoreProps(appUid));
}

export function getIoUsersStore(
  platform: Platform,
  appUid: string,
): Store<IoUsers> | null {
  const props = getIoUsersStoreProps(appUid);
  return platform.getStore<IoUsers>(appUid, props.pageId, props.alias);
}

export function getIoRolesStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoRoles',
    alias: `IoRoles-alias-${appUid}`,
  };
}

export function useIoRolesStore(appUid: string) {
  return useDataStore<IoRoles>(getIoRolesStoreProps(appUid));
}

export function getIoRolesStore(
  platform: Platform,
  appUid: string,
): Store<IoRoles> | null {
  const props = getIoRolesStoreProps(appUid);
  return platform.getStore<IoRoles>(appUid, props.pageId, props.alias);
}

export function useIoProfilesStore(appUid: string) {
  return useDataStore<IoProfiles>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoProfiles',
    alias: `profileAlias_${appUid}`,
  });
}

export function useIoConnectionsStore() {
  return useDataStore<IoConnections>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoConnections',
    alias: 'connectionAlias',
  });
}

export function useIoAgentGroupsStore() {
  return useDataStore<IoAgentGroups>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoAgentGroups',
    alias: 'agentGroupAlias',
  });
}

export function useIoAgentsStore() {
  return useDataStore<IoAgents>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoAgents',
    alias: 'agentsAlias',
  });
}

export function useIoEmailRequestsActiveStore(appUid: string) {
  return useDataStore<IoEmailRequests>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoEmailRequests',
    alias: 'emailRequestsAlias',
  });
}

export function useIoEmailRequestsCompletedStore(appUid: string) {
  return useDataStore<IoEmailRequests>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoEmailRequestsArch',
    alias: 'emailRequestsArchAlias',
  });
}

export function useIoEmailRequestsErrorStore(appUid: string) {
  return useDataStore<IoEmailRequests>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoEmailRequests',
    alias: 'emailRequestsAliasError',
  });
}

export function useIoLanguagesStore(appUid: string) {
  return useDataStore<IoLanguages>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoLanguages',
    alias: 'languagesAlias',
  });
}

export function useIoImageEditStore(appUid: string) {
  return useDataStore<IoImages>(getIoImagesEditStoreProps(appUid));
}

export function getIoImagesEditStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoImages',
    alias: 'IoImagesEditAlias',
  };
}

export function useIoStorageConnectionsStore() {
  return useDataStore<IoStorageConnections>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoStorageConnections',
    alias: 'storageConnectionAlias',
  });
}

export function useIoComponentsStore(appUid: string) {
  return useDataStore<IoComponents>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoComponents',
    alias: 'IoComponents-list',
  });
}

export function getIoAppDevelopersVStoreProps(): StoreProps {
  return {
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoAppDevelopersV',
    alias: 'appDevVAlias',
  };
}

export function getIoUserAppsVStoreProps(): StoreProps {
  return {
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoUserAppsV',
    alias: 'userAppsVAlias',
  };
}

export function getIoAiModelConfigStoreProps(): StoreProps {
  return {
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoAiModelConfig',
    alias: 'AiModelConfigAlias',
  };
}

export function useIoAiModelConfigStore() {
  return useDataStore<any>(getIoAiModelConfigStoreProps());
}

export function getIoAiModelConfigStore(platform: Platform) {
  const props = getIoAiModelConfigStoreProps();
  return platform.getStore<IoAppDevelopersV>(
    props.appUid,
    props.pageId,
    props.alias,
  );
}

export function useIoAppDevelopersVStore() {
  return useDataStore<IoAppDevelopersV>(getIoAppDevelopersVStoreProps());
}

export function useIoUserAppsVStore() {
  return useDataStore<IoAppDevelopersV>(getIoUserAppsVStoreProps());
}

export function getIoAppDevelopersVStore(
  platform: Platform,
): Store<IoAppDevelopersV> | null {
  const props = getIoAppDevelopersVStoreProps();
  return platform.getStore<IoAppDevelopersV>(
    props.appUid,
    props.pageId,
    props.alias,
  );
}

export function getIoWfInstancesStoreProps(
  appUid: string,
  source: WorkflowInstanceSource,
): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: source === 'Active' ? 'IoWfInstances' : 'IoWfInstancesArch',
    alias:
      source === 'Active' ? 'IoWfInstances-alias' : 'IoWfInstancesArch-alias',
  };
}

export function getIoWorkflowNodesStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoWorkflowNodes',
    alias: `IoWorkflowNodes-alias_${appUid}`,
  };
}

export function getIoWorkflowNodesStore(
  platform: Platform,
  appUid: string,
): Store<IoWorkflowNodes> | null {
  const props = getIoWorkflowNodesStoreProps(appUid);
  return platform.getStore<IoWorkflowNodes>(appUid, props.pageId, props.alias);
}

export function useIoWorkflowNodesStore(appUid: string) {
  return useDataStore<IoWorkflowNodes>(getIoWorkflowNodesStoreProps(appUid));
}

export function getIoWorkflowNodesEditStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoWorkflowNodes',
    alias: 'IoWorkflowNodes-editAlias',
  };
}

export function useIoWorkflowNodesEditStore(appUid: string) {
  return useDataStore<IoWorkflowNodes>(
    getIoWorkflowNodesEditStoreProps(appUid),
  );
}

export function getIoWorkflowNodesEditStore(
  platform: Platform,
  appUid: string,
): Store<IoWorkflowNodes> | null {
  const props = getIoWorkflowNodesEditStoreProps(appUid);
  return platform.getStore<IoWorkflowNodes>(appUid, props.pageId, props.alias);
}

export function getIoApplicationsEditStoreProps(): StoreProps {
  return {
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoApplicationsEdit',
    alias: 'applicationEditAlias',
  };
}

export function useIoApplicationsEditStore() {
  return useDataStore<IoApplicationsEdit>(getIoApplicationsEditStoreProps());
}

export function getIoApplicationsEditStore(
  platform: Platform,
): Store<IoApplicationsEdit> | null {
  const props = getIoApplicationsEditStoreProps();
  return platform.getStore<IoApplicationsEdit>(
    props.appUid,
    props.pageId,
    props.alias,
  );
}

export function getIoApplicationsStoreProps(): StoreProps {
  return {
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoApplications',
    alias: 'applicationAlias',
  };
}

export function useIoApplicationsStore() {
  return useDataStore<IoApplications>(getIoApplicationsStoreProps());
}

export function useIoScheduleRunsStore(appUid: string) {
  const store = useDataStore<IoScheduleRuns>(
    getIoScheduleRunsStoreProps(appUid),
  );
  store.ignorePKDuplicate = true;
  return store;
}

export function getIoScheduleRunsStoreProps(appUid: string): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoScheduleRuns',
    alias: `IoScheduleRunsAlias_${appUid}`,
  };
}

export function useIoScheduleRunsArchStore(appUid: string, filter: string) {
  const store = useDataStore<IoScheduleRuns>(
    getIoScheduleRunsArchStoreProps(appUid, filter),
  );
  store.ignorePKDuplicate = true;
  return store;
}

export function useIoAppsStore() {
  return useDataStore<IoApplications>({
    appUid: 'cloudio',
    pageId: '$$Sidebar$$',
    ds: 'IoApplications',
    alias: 'IoApplications-connectionsStatus-alias',
  });
}

export function getIoScheduleRunsArchStoreProps(
  appUid: string,
  filter: string,
): StoreProps {
  return {
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoScheduleRunsArch',
    alias:
      filter === 'Success'
        ? `IoScheduleRunsArchAlias_${appUid}_S`
        : `IoScheduleRunsArchAlias_${appUid}_E`,
  };
}

export function recordId<T>(record: Row<T>): string {
  const id = record._id || record._cid;
  if (!id) {
    throw Error('Missing id!');
  }
  return id;
}

export const languageFilter: (appUid: string) => Query<IoLanguages> = (
  appUid: string,
) => ({
  filter: [{ appUid: { is: appUid } }],
  limit: 5000,
  offset: 0,
  sort: { languageName: 1 },
});

export {
  setValue,
  updateValue,
  push,
  replaceKeyAndValue,
  removeKey,
  inViewBox,
};
